import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import clsx from 'clsx';
import type { ScheduledAction } from '@demind-inc/core';
import { Typography } from '@mui/material';

import { useAllPlanSuggestedModal } from '../../../hooks';
import { ChatSuggestedEvent } from '../../../data-access';
import { transformScheduledActionsToSuggestedEvents } from '../../../data-access/helpers/resultJsonSchedule.helpers';
import PlanDecisionConfetti from '../../Scheduler/AllPlanScheduler/PlanDecisionConfetti';
import { getScheduleId } from '../../Scheduler/AllPlanScheduler/helpers';
import { scheduleFallsOnDate } from '../../../helpers/allPlanSuggestions.helpers';
import { AssistantChatSuggestedBlocks } from '../AssistantChatSuggestedBlocks';
import SchedulerStatusMessages from '../../Chat/SchedulerChatUI/SchedulerStatusMessages';
import '../../Chat/SchedulerChatUI/SchedulerChatUI.scss';
import './PlanPreviewBubble.scss';

const NOOP_PROCESSED_IDS_CHANGE = () => undefined;

/**
 * Live Plan My Day/replan results card — rendered inline in the chat thread
 * (message.allPlanPreview === true) instead of the old dedicated AllPlanPanel
 * sidebar surface. Content is driven entirely by useAllPlanSuggestedModal();
 * items render via the same AssistantChatSuggestedBlocks ("screen" variant —
 * SuggestionGroupCard's Approve All/Dismiss All bar) used by any other
 * grouped chat suggestion, so a plan-my-day item looks and behaves like any
 * other suggested event/routine card rather than its own bespoke design.
 */
const PlanPreviewBubble: React.FC = () => {
  const { t } = useTranslation();
  const {
    isLoading,
    statusLines,
    schedules: suggestedSchedules,
    acceptedIds,
    autoAcceptedIds,
    discardedIds,
    error,
    acceptSchedule,
    discardSchedule,
    targetDate: plannedTargetDate,
  } = useAllPlanSuggestedModal();

  const planDateKey = useMemo(
    () => plannedTargetDate || dayjs().format('YYYY-MM-DD'),
    [plannedTargetDate]
  );

  const sortedSchedules = useMemo(
    () =>
      [...suggestedSchedules].sort((a, b) => {
        const aTime = a.startDate ? new Date(a.startDate).getTime() : 0;
        const bTime = b.startDate ? new Date(b.startDate).getTime() : 0;
        return aTime - bTime;
      }),
    [suggestedSchedules]
  );

  // Resolve ids from the unsorted atom list so `plan-${index}` (legacy) and
  // durable schedule.id stay aligned with CalendarTimeline / cache decisions.
  const scheduleIdByRef = useMemo(() => {
    const map = new Map<ScheduledAction, string>();
    suggestedSchedules.forEach((schedule, index) => {
      map.set(schedule, getScheduleId(schedule, index));
    });
    return map;
  }, [suggestedSchedules]);

  const scheduleEntries = useMemo(
    () =>
      sortedSchedules.map((schedule) => ({
        schedule,
        scheduleId: scheduleIdByRef.get(schedule) ?? getScheduleId(schedule, 0),
      })),
    [scheduleIdByRef, sortedSchedules]
  );

  // Preview is date-scoped — only show items for the plan target date.
  const visibleScheduleEntries = useMemo(
    () => scheduleEntries.filter(({ schedule }) => scheduleFallsOnDate(schedule, planDateKey)),
    [planDateKey, scheduleEntries]
  );

  const visibleBlocks = useMemo(
    () =>
      transformScheduledActionsToSuggestedEvents(visibleScheduleEntries.map((e) => e.schedule)),
    [visibleScheduleEntries]
  );

  const scheduleByEventId = useMemo(
    () => new Map(visibleScheduleEntries.map((e) => [e.scheduleId, e.schedule])),
    [visibleScheduleEntries]
  );

  const showLoading = isLoading || statusLines.length > 0;
  const showResults = !isLoading && visibleBlocks.length > 0;
  const isLoadingBare = showLoading && !showResults;
  const [showConfetti, setShowConfetti] = useState(false);

  const maybeShowConfettiForFinalDecision = (scheduleId: string) => {
    if (!visibleScheduleEntries.length) return;

    const hasDecision = acceptedIds.includes(scheduleId) || discardedIds.includes(scheduleId);
    if (hasDecision) return;

    const pendingOnDate = visibleScheduleEntries.filter(
      ({ scheduleId: id }) => !acceptedIds.includes(id) && !discardedIds.includes(id)
    ).length;
    if (pendingOnDate <= 1) {
      setShowConfetti(true);
    }
  };

  // Adapter: AssistantChatSuggestedEventBlock/RoutineBlock's accept flow
  // expects an onManageCalendar(block) => Promise<boolean> callback (its
  // usual job is creating/updating a real calendar event) — here it instead
  // accepts the underlying plan-my-day schedule as-is via acceptSchedule,
  // the same action PlanItemCard used to trigger.
  const handleManageCalendar = async (block: ChatSuggestedEvent): Promise<boolean> => {
    const schedule = scheduleByEventId.get(block.eventId);
    if (!schedule) {
      return false;
    }
    maybeShowConfettiForFinalDecision(block.eventId);
    await acceptSchedule(block.eventId, schedule);
    return true;
  };

  // Discard (including Dismiss All) should not celebrate — confetti is only
  // for finishing the plan by accepting the last pending item.
  const handleDiscardBlock = (eventId: string) => {
    discardSchedule(eventId);
  };

  const handleDiscardAllBlocks = (eventIds: string[]) => {
    eventIds.forEach((id) => discardSchedule(id));
  };

  useEffect(() => {
    if (!showConfetti) return;
    const timeoutId = window.setTimeout(() => setShowConfetti(false), 2800);
    return () => window.clearTimeout(timeoutId);
  }, [showConfetti]);

  const body = (
    <div
      className={clsx('plan-preview-bubble', {
        'plan-preview-bubble--loading-bare': isLoadingBare,
      })}
    >
      <PlanDecisionConfetti isVisible={showConfetti} />

      {showLoading && (
        <SchedulerStatusMessages
          statusLines={statusLines}
          emptyFallback={
            isLoading
              ? t('task.allPlan.analyzingSchedule', {
                  defaultValue: 'Analyzing your schedule…',
                })
              : undefined
          }
        />
      )}

      {showResults && (
        <AssistantChatSuggestedBlocks
          blocks={visibleBlocks}
          processedIds={[...acceptedIds, ...discardedIds]}
          onProcessedIdsChange={NOOP_PROCESSED_IDS_CHANGE}
          onManageCalendar={handleManageCalendar}
          variant="screen"
          onDiscardBlock={handleDiscardBlock}
          onDiscardAllBlocks={handleDiscardAllBlocks}
          autoAcceptedIds={new Set(autoAcceptedIds)}
        />
      )}

      {!showLoading && !showResults && (
        <div className="plan-preview-bubble__empty">
          <Typography variant="caption" color={error ? 'error' : 'text.secondary'}>
            {error ?? t('task.allPlan.noSuggestions')}
          </Typography>
        </div>
      )}
    </div>
  );

  // Loading status matches v4 sequence-status: no lavender bubble chrome.
  if (isLoadingBare) {
    return body;
  }

  return (
    <div className="assistant-chat-content-container__bubble assistant-chat-content-container__bubble--fill">
      <div className="assistant-chat-content-container__bubble-content">{body}</div>
    </div>
  );
};

export default PlanPreviewBubble;
